// TerrainDescriptor → 三角形メッシュ（頂点 + インデックス）の構築。
//
// 描画（threeRenderer の terrainView）と物理（physicsController の trimesh
// コライダー）の両方がここを使い、見た目と当たり判定の地形を完全に一致させる。
//
// 頂点はセル中心に 1 つずつ置き（vertexIndex = zIndex * width + xIndex）、
// 隣接 4 セルが全て有効なクワッドへ三角形 2 枚を張る。穴セルに接するクワッドは
// 張らない。穴セルの頂点はインデックスから参照されないが、バウンディング計算が
// 番兵値で壊れないよう有効セルの最低高へ寄せておく。
// セル中心方式のため、メッシュ範囲はグリッド外周より半セル内側になる
// （types.ts の TerrainDescriptor 参照）。
//
// 崖（隣接セルの大きな高低差）も 1 クワッドの急斜面としてそのまま張る。
// 登れるかどうかは物理側の登坂角制限（Roblox の Humanoid.MaxSlopeAngle 既定
// 89° に合わせた値）に委ねる。実機の smooth terrain も 89° 未満の急斜面は
// 歩いて登れるため、ここで段差の上限を設けると実機より登れない地形になる。
import { TERRAIN_HOLE_HEIGHT, TerrainDescriptor } from './types'

/** heights の値が「地形なし（穴）」かどうか。 */
export const isTerrainHole = (height: number): boolean =>
  height <= TERRAIN_HOLE_HEIGHT

export interface TerrainGeometryData {
  /** 頂点座標 (x, y, z) × (width * depth)。セル中心、行優先。 */
  positions: Float32Array
  /** 三角形インデックス（反時計回り = 上向きの面）。 */
  indices: Uint32Array
}

/**
 * 高さマップから三角形メッシュを構築する。三角形が 1 枚も張れない
 * （有効セルが無い・孤立している）場合は null。
 */
export const buildTerrainGeometry = (
  terrain: TerrainDescriptor,
): TerrainGeometryData | null => {
  const { width, depth, heights, cellSize, originX, originZ } = terrain
  const positions = new Float32Array(width * depth * 3)

  let minValidHeight = Infinity
  for (const height of heights) {
    if (!isTerrainHole(height)) {
      minValidHeight = Math.min(minValidHeight, height)
    }
  }
  if (!Number.isFinite(minValidHeight)) {
    // 有効セルなし。三角形も張れないため後段で null になるが、頂点座標が
    // Infinity にならないよう安全な値へ倒しておく。
    minValidHeight = 0
  }

  for (let iz = 0; iz < depth; iz += 1) {
    for (let ix = 0; ix < width; ix += 1) {
      const cell = iz * width + ix
      const height = heights[cell]
      const base = cell * 3
      positions[base] = originX + (ix + 0.5) * cellSize
      positions[base + 1] = isTerrainHole(height) ? minValidHeight : height
      positions[base + 2] = originZ + (iz + 0.5) * cellSize
    }
  }

  const indices: number[] = []
  for (let iz = 0; iz < depth - 1; iz += 1) {
    for (let ix = 0; ix < width - 1; ix += 1) {
      const a = iz * width + ix
      const b = a + 1
      const c = a + width
      const d = c + 1
      if (
        isTerrainHole(heights[a]) ||
        isTerrainHole(heights[b]) ||
        isTerrainHole(heights[c]) ||
        isTerrainHole(heights[d])
      ) {
        continue
      }
      indices.push(a, c, b, b, c, d)
    }
  }
  if (indices.length === 0) {
    return null
  }
  return { positions, indices: new Uint32Array(indices) }
}
